import React, { useCallback, useEffect, useState } from 'react';
import { Card, Title, Text, Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell, Badge } from '@tremor/react';
import Header from '../components/layout/Header';
import { taliazBotV1Api, TaliazBotV1Event } from '../services/api';

const POLL_INTERVAL_MS = 5000;

export default function TaliazBotV1Events() {
  const [events, setEvents] = useState<TaliazBotV1Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    taliazBotV1Api
      .list()
      .then((res) => setEvents(res.data.events))
      .catch((err) => setError(err.response?.data?.detail || err.message || 'Failed to load events'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="mx-auto max-w-6xl p-6">
        <Card>
          <div className="flex items-center justify-between">
            <Title>TALIAZ-BOTv1 Webhook Events</Title>
            {!loading && !error && <Text>{events.length} received</Text>}
          </div>

          {loading && <Text className="mt-6">Loading…</Text>}
          {error && <Text className="mt-6 text-red-500">{error}</Text>}

          {!loading && !error && events.length === 0 && (
            <Text className="mt-6">No webhook calls received yet.</Text>
          )}

          {!loading && !error && events.length > 0 && (
            <Table className="mt-6">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Received At</TableHeaderCell>
                  <TableHeaderCell>Payload</TableHeaderCell>
                  <TableHeaderCell>Bot Reply</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((e, i) => (
                  <TableRow key={i}>
                    <TableCell className="whitespace-nowrap align-top">
                      {new Date(e.received_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="align-top">
                      <pre className="whitespace-pre-wrap break-all text-xs">
                        {JSON.stringify(e.payload, null, 2)}
                      </pre>
                    </TableCell>
                    <TableCell className="align-top">
                      {e.bot_reply ? (
                        <div className="space-y-1">
                          <div className="flex gap-1 flex-wrap">
                            {e.bot_reply.intent && <Badge color="blue">{e.bot_reply.intent}</Badge>}
                            <Badge color="gray">{e.bot_reply.source}</Badge>
                            <Badge color={e.bot_reply.hm_id ? 'emerald' : 'yellow'}>
                              {e.bot_reply.hm_id ? `hm_id: ${e.bot_reply.hm_id}` : 'no patient match'}
                            </Badge>
                            {e.bot_reply.confidence > 0 && (
                              <Badge color="violet">conf {e.bot_reply.confidence.toFixed(2)}</Badge>
                            )}
                          </div>
                          <Text className="text-xs">{e.bot_reply.reply}</Text>
                        </div>
                      ) : (
                        <Text className="text-xs text-gray-400">— (still processing, or waId/text missing)</Text>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </main>
    </div>
  );
}
